import { TFunction } from 'i18next'
import React from 'react'
import { join, uniqBy } from 'lodash'

import { MetainformationColumns } from '../ci/getCiDefaultMetaAttributes'

import { ATTRIBUTE_NAME } from '@isdd/metais-common/api/constants'
import { DISPLAY_AS } from '@isdd/metais-common/constants'
import { ConfigurationItemUi } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { ConfigurationItemUi as ConfigurationItemUiOriginal } from '@isdd/metais-common/api/generated/iam-swagger'
import { Attribute, AttributeConstraintEnum } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { Link } from 'react-router-dom'

const findUnitValue = (attribute: Attribute | undefined, unitsData: EnumType | undefined) => {
    const unit = unitsData?.enumItems?.find((item) => item.code === attribute?.units)?.value ?? ''
    if (unit.toLowerCase().includes('eur')) return 'Eur'
    return unit
}

type FormatRowValueByRowArgs = {
    attribute: Attribute | undefined
    rowValue: string
    t: TFunction<'translation', undefined>
    unitsData: EnumType | undefined
}

const formatRowValueByRowType = ({ attribute, rowValue, t, unitsData }: FormatRowValueByRowArgs) => {
    if (attribute?.units && attribute?.type && rowValue) {
        const unitValue = findUnitValue(attribute, unitsData)
        const PERCENT_SIGN = '%'
        const GB_PER_HOUR = 'GB/h'
        if (unitValue === PERCENT_SIGN) {
            return t(`units.percent`, { val: rowValue })
        }
        if (unitValue === GB_PER_HOUR) {
            return t(`units.GBperHour`, { val: rowValue })
        }
        return t(`units.${unitValue}`, { val: rowValue })
    }
    switch (attribute?.type) {
        case 'BOOLEAN':
            return rowValue ? t('radioButton.yes') : t('radioButton.no')
        case 'DATE':
            if (rowValue) {
                return t('date', { date: rowValue })
            }
            return rowValue
        case 'DATETIME':
            if (rowValue) {
                return t('dateTime', { date: rowValue })
            }
            return rowValue
        default:
            return rowValue
    }
}

type PairEnumstoEnumValuesArgs = {
    attribute: Attribute | undefined
    ciItemData: ConfigurationItemUi | ConfigurationItemUiOriginal | undefined
    constraintsData: (EnumType | undefined)[] | undefined
    t: TFunction<'translation', undefined>
    unitsData: EnumType | undefined
    matchedAttributeNamesToCiItem: Record<string, ConfigurationItemUi | ConfigurationItemUi[]> | undefined
    withDescription?: boolean
    isFromTable?: boolean
}

export const pairEnumsToEnumValues = ({
    attribute,
    ciItemData,
    constraintsData,
    t,
    unitsData,
    matchedAttributeNamesToCiItem,
    withDescription,
    isFromTable = false,
}: PairEnumstoEnumValuesArgs) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let rowValue: any
    if (Array.isArray(ciItemData?.attributes)) {
        rowValue = ciItemData?.attributes?.find((i) => i.name === attribute?.technicalName || i.name === attribute?.name)?.value || ''
    } else {
        rowValue = ciItemData?.attributes?.[attribute?.technicalName ?? attribute?.name ?? '']
    }
    const formattedRowValue = formatRowValueByRowType({ attribute, rowValue, t, unitsData })

    // when displayAs is set to 'raw', skip the enum lookup and show the value as it comes from BE, same as an attribute without constraints
    if (attribute?.displayAs === DISPLAY_AS.RAW || !attribute?.constraints || !attribute?.constraints?.length) {
        if (!isFromTable && attribute?.isArray && Array.isArray(formattedRowValue) && formattedRowValue.length > 1) {
            return (
                <ul style={{ margin: 0 }}>
                    {formattedRowValue?.map((value, index) => (
                        <li key={`${attribute.id}_${index}`}>{value}</li>
                    ))}
                </ul>
            )
        }
        return formattedRowValue
    }
    return (
        uniqBy(attribute?.constraints, 'enumCode')?.map((constraint) => {
            switch (constraint?.type) {
                case 'enum': {
                    const recastConstraint = constraint as AttributeConstraintEnum
                    const foundEnumByCode = constraintsData?.find((cD) => cD?.code === recastConstraint?.enumCode)
                    const isRowValueArray = Array.isArray(rowValue)
                    const foundEnumItems = foundEnumByCode?.enumItems?.filter((enumItem) =>
                        isRowValueArray ? rowValue?.indexOf(enumItem?.code) !== -1 : enumItem?.code === rowValue,
                    )

                    if (withDescription)
                        return foundEnumItems?.map((enumItem, index) => {
                            const extraSpace = index === 0 ? '' : ' '
                            if (enumItem?.description && enumItem.description != enumItem.value)
                                return `${extraSpace}${enumItem.value}${enumItem.description ? ' - ' + enumItem.description : ''}`
                            return [enumItem?.value]
                        })
                    return foundEnumItems?.flatMap((enumItem) => [enumItem.value])
                }
                case 'interval': {
                    return parseInt(formattedRowValue ?? 0) ?? 0
                }
                case 'ciType': {
                    if (Array.isArray(matchedAttributeNamesToCiItem?.[attribute.technicalName ?? ''])) {
                        return join(
                            (matchedAttributeNamesToCiItem?.[attribute.technicalName ?? ''] as ConfigurationItemUi[]).map((ci) => {
                                return ci.attributes?.[ATTRIBUTE_NAME.Gen_Profil_nazov]
                            }),
                            ', ',
                        )
                    } else {
                        return (
                            (matchedAttributeNamesToCiItem?.[attribute.technicalName ?? ''] as ConfigurationItemUi)?.attributes?.[
                                ATTRIBUTE_NAME.Gen_Profil_nazov
                            ] ?? ''
                        )
                    }
                }
                default: {
                    return formattedRowValue
                }
            }
        }) ?? []
    )?.join(',')
}

export const pairFormularValuesAndTypyPriloh = ({ attribute, matchedAttributeNamesToCiItem }: PairEnumstoEnumValuesArgs) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    const ciList = matchedAttributeNamesToCiItem?.[attribute?.technicalName ?? '']

    if (Array.isArray(ciList)) {
        return (
            <ul>
                {ciList.map((ci) => (
                    <li key={ci.uuid}>
                        <Link target="_blank" rel="noopener noreferrer" to={`/ci/${ci.type}/${ci.uuid}`}>
                            {ci.attributes?.[ATTRIBUTE_NAME.Gen_Profil_nazov] as string}
                        </Link>
                    </li>
                ))}
            </ul>
        )
    } else {
        return (
            (matchedAttributeNamesToCiItem?.[attribute?.technicalName ?? ''] as ConfigurationItemUi)?.attributes?.[ATTRIBUTE_NAME.Gen_Profil_nazov] ??
            ''
        )
    }
}

export const distinctAttributesMetaAttributes = (
    attributes: Attribute[],
    metaAttributes: {
        name: string
        attributes: {
            name: string
            technicalName: MetainformationColumns
        }[]
    },
) => {
    return attributes?.filter((attr) => !metaAttributes?.attributes?.find((metaAttribute) => metaAttribute?.technicalName === attr?.technicalName))
}
